import { useState, CSSProperties, useCallback, useEffect } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';
import { SwapVert } from '@material-ui/icons';
import { useNavigate } from 'react-router';
import {
  Button,
  Card,
  Stack,
  TextField,
  CardContent,
  IconButton,
  useTheme,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import dayjs from 'dayjs';

import { FormProvider, Iconify, isOverDue, Scrollbar, StatusTag } from '@app/components';
import { useDialog, useMasterFilter, useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { CustomAvatar, CustomAvatarGroup } from '@app/components/custom-avatar';
import { selectMyProfileFormState } from '@app/modules/my-profile';
import { useAuthRole, useAuthRolePermissions } from '@app/utils';
import { CardDetails } from '../taskBoardDetails/card-details';
import { ICard, IDeleteCardSchema, IList, IMember } from '../types';
import { AddCardAndList } from './add-card-and-list';
import { RowActions } from './row-actions';
import { SideBar } from './side-bar';
import { TopBar } from './top-bar';
import { StyledMainStack, Heading, StyledImageStack } from '../styles';
import {
  selectAddCardState,
  selectBoardDetailsState,
  selectMoveCardState,
  selectTaskBoardState,
} from '../selector';
import { addCard, getBoardDetailsSuccess, getTaskBoard, moveCard, setStatus } from '../slice';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { connectToBoardSocket, disconnectBoardSocket, emitBoardUpdate } from '../utils';

const TaskBoardCard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { isSuperAdmin } = useAuthRole();

  const { data } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });

  useRequestState({
    stateSelector: selectMoveCardState,
    errorShown: true,
  });

  const { hasPermission } = useAuthRolePermissions();
  const canAddCard = hasPermission('Cards', 'w');
  const canAddList = hasPermission('Lists', 'w');

  const [ids, setIds] = useState<IDeleteCardSchema>();
  const [newItemContent, setNewItemContent] = useState('');
  const [addingItemToListId, setAddingItemToListId] = useState<string | null>(null);
  const [showListTexfield, setShowListTexfield] = useState(false);
  const [selectedManuallyImage, setSelectedManuallyImage] = useState('');
  const [showAddAnotherList, setShowAddAnotherList] = useState(false);

  const handleClosePopup = useCallback(() => {
    setAddingItemToListId(null);
  }, [setAddingItemToListId]);

  const { loading } = useRequestState({
    stateSelector: selectAddCardState,
    onSuccess: handleClosePopup,
    errorShown: true,
  });

  const taskBoardDialog = useDialog();
  const methods = useForm();
  const navigate = useNavigate();

  useEffect(() => {
    navigate(`${PATH_DASHBOARD.taskBoard}/${String(data?.name)}`, {
      replace: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.name]);
  const { data: myProfile } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });

  const { data: TaskBoardData } = useRequestState({
    stateSelector: selectTaskBoardState,
    errorShown: true,
  });

  const { filters, setFilters, filteredLists, handleSortToggle } = useMasterFilter({
    lists: data?.lists,
    myProfileId: myProfile?.id,
    boardId: data?.id,
  });
  useEffect(() => {
    if (!data?.id) return;

    connectToBoardSocket(
      data.id,
      (socketData) => {
        if ('lists' in socketData) {
          dispatch(getBoardDetailsSuccess(socketData));
        } else {
          dispatch(moveCard(socketData));
        }
      },
      (status) => dispatch(setStatus(status as 'idle' | 'connected' | 'disconnected')),
      (joinedRoomData) => {
        console.log('Joined:', joinedRoomData);
      }
    );

    return () => {
      disconnectBoardSocket();
    };
  }, [data?.id, dispatch]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.droppableId === result.source.droppableId) return;

    const payload = {
      boardId: data.id,
      existingListId: Number(result.source.droppableId),
      newListId: Number(result.destination.droppableId),
      cardId: Number(result.draggableId),
    };

    emitBoardUpdate(payload);
    dispatch(moveCard(payload));
  };

  const getItemStyle = (isDragging: boolean, draggableStyle: CSSProperties): CSSProperties => ({
    ...draggableStyle,
    userSelect: isDragging ? 'none' : 'auto',
  });

  const handleCardClick = (cardId: string, listId: string) => {
    setIds({ cardId, listId, boardId: data?.id });
    navigate(`${PATH_DASHBOARD.taskBoard}/${String(data?.name)}/${listId}/${cardId}`, {
      replace: false,
    });
    taskBoardDialog.show();
  };

  const [editing, setEditing] = useState<string | null>(null);

  const handleEditing = (listId: string) => () => {
    setShowAddAnotherList(false);
    setEditing(listId);
    setShowListTexfield(true);
  };

  const { data: userData } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });

  useEffect(() => {
    if (userData) {
      dispatch(getTaskBoard({ userId: userData?.id }));
    }
  }, [dispatch, userData]);

  const isLoggedInUser = data?.members?.some((member: any) => {
    if (isSuperAdmin) {
      return true;
    } else if (member.membersUserId === myProfile?.id && member.isAdmin) {
      return true;
    }
    return false;
  });

  return (
    <StyledMainStack
      image={TaskBoardData?.length !== 0 ? data?.image : '/assets/images/NoBoardFound.png'}
      colors="image-6"
      selectedManuallyImage={selectedManuallyImage}
    >
      <Stack sx={{ backgroundAttachment: 'fixed' }}>
        <FormProvider methods={methods}>
          <TopBar id={data?.projectId} filters={filters} setFilters={setFilters} />
        </FormProvider>
      </Stack>
      <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
        <Stack
          sx={{
            height: '100vh',
            width: '100%',
            overflow: 'auto',
          }}
        >
          <DragDropContext onDragEnd={handleDragEnd}>
            <Stack direction={'row'} gap={2} p={'25px 25px 25px 10px'}>
              {filteredLists?.map((list: IList, index: number) => (
                <Droppable key={String(list.id)} droppableId={String(list.id)}>
                  {(provided) => (
                    <Stack
                      style={{
                        background: theme.palette.grey[200],
                        borderRadius: '10px',
                        alignItems: 'flex-start',
                        maxHeight: '400px',
                        height: 'fit-content',
                      }}
                      px={1}
                    >
                      <Heading>
                        {editing !== list.id ? (
                          <Stack
                            sx={{ width: '90%' }}
                            flexDirection={'row'}
                            gap={2}
                            alignItems={'center'}
                          >
                            <h4 style={{ wordWrap: 'break-word' }}>
                              {list?.name}
                              {list?.cards?.length ? ` - ${list.cards.length}` : ''}
                            </h4>
                            {list?.cards?.length > 0 && (
                              <Tooltip title={'sort asc/desc'}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    handleSortToggle(list.id);
                                  }}
                                >
                                  <SwapVert />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        ) : (
                          <AddCardAndList
                            handleCloseList={() => {
                              setShowListTexfield(false);
                              setEditing(null);
                            }}
                            setEditing={setEditing}
                            showListTexfield={showListTexfield}
                            setShowListTexfield={setShowListTexfield}
                            boardId={data?.id}
                            listId={Number(list?.id)}
                            listIndex={index}
                            data={list}
                            isLoggedInUser={isLoggedInUser}
                          />
                        )}
                        {canAddList && editing !== list.id && (
                          <RowActions setEditing={handleEditing(list.id)} listId={list?.id} />
                        )}
                      </Heading>
                      <Scrollbar
                        sx={{
                          '&::-webkit-scrollbar': { display: 'none' },
                          scrollbarWidth: 'none',
                          '-ms-overflow-style': 'none',
                        }}
                      >
                        <Stack
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          minWidth={'250px'}
                          sx={{ height: 'fit-content' }}
                        >
                          {list?.cards?.map((item: ICard, index: number) => {
                            const coverImage = item?.attachments?.find(
                              (attachment) => attachment?.isCoverImage
                            );
                            const formattedDate = `${
                              item?.dueDates?.startDate
                                ? dayjs(item?.dueDates?.startDate).format('MMM DD ') + '- '
                                : ''
                            }${
                              item?.dueDates?.endDate
                                ? dayjs(item?.dueDates?.endDate).format('MMM DD') + ' '
                                : ''
                            }`;
                            const endDate: any = item?.dueDates?.endDate?.split('T')[0];
                            const endTime: any = item?.dueDates?.endTime?.split('T')[1];
                            const dueStatus = isOverDue(endDate, endTime);
                            return (
                              <Draggable
                                key={String(item.id)}
                                draggableId={String(item.id)}
                                index={index}
                              >
                                {(
                                  provided: DraggableProvided,
                                  snapshot: DraggableStateSnapshot
                                ) => (
                                  <Card
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{
                                      ...getItemStyle(
                                        snapshot.isDragging,
                                        provided.draggableProps.style as CSSProperties
                                      ),
                                      marginBottom: '8px',
                                      borderRadius: '10px',
                                      width: '250px',
                                      padding: '8px',
                                    }}
                                  >
                                    <CardContent
                                      onClick={() => {
                                        handleCardClick(item?.id, list?.id);
                                      }}
                                      sx={{
                                        padding: 0,
                                      }}
                                    >
                                      {coverImage?.attachment && (
                                        <StyledImageStack
                                          url={coverImage?.attachment}
                                          height={'120px'}
                                          width={'100%'}
                                        />
                                      )}
                                      <Stack
                                        direction="row"
                                        spacing={0.1}
                                        alignItems={'center'}
                                        sx={{
                                          justifyContent: 'flex-end',
                                          alignContent: 'center',
                                          mt: 1,
                                        }}
                                      >
                                        {item?.addedLabels?.map((label, index) => (
                                          <Tooltip key={label.id || index} title={label.name}>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                width: '35px',
                                                height: '7px',
                                                bgcolor: label.color,
                                                borderRadius: '20px',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                verticalAlign: 'center',
                                              }}
                                            ></Typography>
                                          </Tooltip>
                                        ))}
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          justifyContent: 'center',
                                        }}
                                      >
                                        {item?.name}
                                      </Typography>
                                      <Stack
                                        sx={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          position: 'relative',
                                          top: 24,
                                          alignItems: 'flex-end',
                                          flexWrap: 'wrap',
                                          gap: 1,
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {formattedDate && (
                                          <Box sx={{ marginLeft: 'auto' }}>
                                            <Stack
                                              sx={{
                                                backgroundColor: item?.dueDates?.isCompleted
                                                  ? theme.palette.success.dark
                                                  : dueStatus === 'dueSoon'
                                                  ? theme.palette.warning.light
                                                  : dueStatus === 'overDue'
                                                  ? theme.palette.error.lighter
                                                  : '',
                                                px: 0.3,
                                                py: 0.33,
                                                borderRadius: '4px',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                textWrap: 'nowrap',
                                                alignItems: 'center',
                                                gap: 0.7,
                                              }}
                                            >
                                              <Iconify
                                                icon="il:clock"
                                                sx={{
                                                  height: 15,
                                                  width: 15,
                                                  color: item?.dueDates?.isCompleted
                                                    ? theme.palette.common.white
                                                    : dueStatus === 'dueSoon'
                                                    ? theme.palette.common.black
                                                    : dueStatus === 'overDue'
                                                    ? theme.palette.warning.darker
                                                    : theme.palette.common.black,
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  fontSize: '12px',
                                                  color: item?.dueDates?.isCompleted
                                                    ? theme.palette.common.white
                                                    : dueStatus === 'dueSoon'
                                                    ? theme.palette.common.black
                                                    : dueStatus === 'overDue'
                                                    ? theme.palette.warning.darker
                                                    : theme.palette.common.black,
                                                }}
                                              >
                                                {formattedDate}
                                              </Typography>
                                            </Stack>
                                          </Box>
                                        )}

                                        <Box sx={{ ml: '5px' }}>
                                          {item.dueDates === null ? null : (
                                            <StatusTag status={item?.dueDates?.status} />
                                          )}
                                        </Box>
                                        <Box
                                          sx={{
                                            maxWidth: 100,
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                          }}
                                        >
                                          <CustomAvatarGroup
                                            max={3}
                                            size="tiny"
                                            style={{ gap: '3px' }}
                                            sx={{
                                              color: theme.palette.common.white,
                                              '& .MuiAvatarGroup-avatar': {
                                                backgroundColor:
                                                  theme.palette.taskboard.raisinBlack,
                                                color: theme.palette.common.white,
                                                '&:first-of-type': {
                                                  backgroundColor:
                                                    theme.palette.taskboard.raisinBlack,
                                                  color: theme.palette.common.white,
                                                },
                                              },
                                            }}
                                          >
                                            {item?.members?.length > 0 &&
                                              item?.members?.map(
                                                (member: IMember, index: number) => (
                                                  <CustomAvatar
                                                    key={index}
                                                    name={`${member?.firstName} ${member?.lastName} `}
                                                    color="info"
                                                    src={member?.profileImage}
                                                  />
                                                )
                                              )}
                                          </CustomAvatarGroup>
                                        </Box>
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                )}
                              </Draggable>
                            );
                          })}
                          {list.cards.length === 0 && (
                            <div style={{ minHeight: '1px', background: 'transparent' }} />
                          )}
                        </Stack>
                      </Scrollbar>

                      <Stack>
                        {canAddCard && addingItemToListId !== list.id && (
                          <Button
                            color="inherit"
                            onClick={() => {
                              setAddingItemToListId(list.id);
                            }}
                          >
                            {Strings.taskBoard.addACard}
                          </Button>
                        )}

                        {addingItemToListId === list.id && (
                          <Stack spacing={1} mb={1}>
                            <TextField
                              type="text"
                              value={newItemContent}
                              sx={{
                                backgroundColor: 'white',
                                borderRadius: '10px',
                                width: '112%',
                                wordWrap: 'break-word',
                              }}
                              onChange={(e) => {
                                setNewItemContent(e.target.value);
                              }}
                              placeholder="Add new item"
                            />

                            <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
                              <LoadingButton
                                loading={loading}
                                color="inherit"
                                sx={{
                                  backgroundColor: theme.palette.taskboard.raisinBlack,
                                  width: '40%',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: theme.palette.taskboard.raisinBlack,
                                    color: theme.palette.error.contrastText,
                                  },
                                }}
                                onClick={() => {
                                  dispatch(
                                    addCard({
                                      listId: Number(addingItemToListId),
                                      name: newItemContent,
                                      boardId: data?.id,
                                    })
                                  );
                                  setNewItemContent('');
                                }}
                                disabled={!newItemContent?.trim()}
                              >
                                {Strings.taskBoard.addCard}
                              </LoadingButton>
                              <IconButton
                                aria-label="close"
                                onClick={() => {
                                  setAddingItemToListId(null);
                                }}
                                sx={{
                                  color: (theme) => theme.palette.common.black,
                                }}
                              >
                                <Iconify icon={'material-symbols:close'} />
                              </IconButton>
                            </Stack>
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </Droppable>
              ))}
              {canAddList && TaskBoardData?.length !== 0 && (
                <AddCardAndList
                  handleCloseList={() => {
                    setShowAddAnotherList(false);
                  }}
                  setEditing={setEditing}
                  showListTexfield={showAddAnotherList}
                  setShowListTexfield={setShowAddAnotherList}
                  boardId={data?.id}
                  listIndex={data?.lists?.length}
                />
              )}
            </Stack>
          </DragDropContext>
        </Stack>
        <Stack justifyContent={'center'}>
          {TaskBoardData?.length !== 0 && (
            <SideBar
              data={data}
              setSelectedManuallyImage={setSelectedManuallyImage}
              isLoggedInUser={isLoggedInUser}
            />
          )}
        </Stack>
      </Stack>

      {taskBoardDialog.visible && (
        <CardDetails
          ids={ids}
          open
          onClose={() => {
            navigate(`${PATH_DASHBOARD.taskBoard}/${String(data.name)}`, {
              replace: false,
            });
            taskBoardDialog.hide();
          }}
          data={data}
          isLoggedInUser={isLoggedInUser}
        />
      )}
    </StyledMainStack>
  );
};

export { TaskBoardCard };
